//カードテンプレートデザイン編集画面
import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux';
import Split from 'react-split';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { Button, IconButton } from '@material-ui/core'
import LockIcon from '@material-ui/icons/Lock'

import { CardTemplateDb } from '../redux/cardTemplateDbSlice';
import { CardTemplateAttributeDb } from '../redux/cardTemplateAttributeDbSlice';
import { CardTemplateShapeDb } from '../redux/cardTemplateShapeDbSlice';
import { UserCondition, updateUserCondition } from '../redux/userConditionSlice';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useOneCardTemplateValue from '../hooks/useOneCardTemplateValue';
import useCardTemplateAttributeDbs from '../hooks/useCardTemplateAttributeDbs';
import useCardTemplateShapeDbs from '../hooks/useCardTemplateShapeDbs';
import useUserCondition from '../hooks/useUserCondition';
import useWindowSize from '@rehooks/window-size';
import useElementDimensions from '../hooks/useElementDimensions';

import { deepCopy } from '../general';
import { initial } from '../card_components'
import EditTemplateCard from '../card_components/EditTemplateCard'
import PermissionDialog from './PermissionDialog'
import SaveTemplateDialog from './SaveTemplateDialog'
import Note, { OpenNoteButton } from './Note';

export type EditTemplateAttributeDbHashType = { [key in string]: CardTemplateAttributeDb | null };
export type EditTemplateShapeDbHashType = { [key in string]: CardTemplateShapeDb | null };

const useStyles = makeStyles(() => ({
  split: {
    display: 'flex',
    height: '100%',
    cursor: 'ew-resize',
    zIndex: 2,
    position: 'relative',
  },
  disableSplit: {
    display: 'flex',
    height: '100%',
    cursor: 'auto',
    zIndex: 2,
    position: 'relative',
  },
  workPanel: {
    flex: 'auto',
    cursor: 'auto',
    zIndex: 0,
    position: 'relative',
  },
  notePanel: {
    flex: 'auto',
    overflow: 'hidden',
    cursor: 'auto',
    zIndex: 1,
    position: 'relative',
    height: 'inherit',
  },
}));

const getSplitRatios = (condition?: UserCondition) => {
  if (!condition) return [70, 30];
  const ratios = condition.workNoteRatios.cardTemplateDesign;
  const workRatio = Number.isFinite(ratios[0]) ? ratios[0] : 70;
  const noteRatio = Number.isFinite(ratios[1]) ? ratios[1] : 30;
  if (condition.noteDisplay.cardTemplateDesign) return [workRatio, noteRatio];
  else return [100];
}

type EditTemplateDialogProps = {
  open: boolean
  onClose: () => void
  templateId: string | undefined// undefinedの場合は、新規追加
  paletteId: string;
  cardTemplatesLength: number;
}
const EditTemplateDialog:React.FC<EditTemplateDialogProps> = ({ open, onClose, templateId, paletteId, cardTemplatesLength }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const editDialogContentId = `edit-template-dialog-content`;
  const id = templateId ? templateId : 'newTemplate';
  const userId = useCurrentUserId() || 'no user';
  const existingTemplate = useOneCardTemplateValue(id)
  const templateValue: CardTemplateDb = existingTemplate ? existingTemplate : { name: '新規カードテンプレート', kind: 'common', author: userId, standard: false, defaultWidth: initial.cardWidth, defaultHeight: initial.cardHeight, frame: {display: true, color: '#000000'}, background: {display: true, color: '#ffffff'}, shadow: true, notes: '', isGroup: false };
  const [attributeIds, attributeValues] = useCardTemplateAttributeDbs(id);
  const [shapeIds, shapeValues] = useCardTemplateShapeDbs(id);
  const [template, setTemplate] = useState<CardTemplateDb | undefined>(undefined)
  const [attributes, setAttributes] = useState<EditTemplateAttributeDbHashType>({});
  const [shapes, setShapes] = useState<EditTemplateShapeDbHashType>({});
  const [permissionAction, setPermissionAction] = useState({show: false})
  const [saveAction, setSaveAction] = useState({show: false})
  const [userConditionId, userCondition] = useUserCondition(userId);
  const [elementDrawn, setElementDrawn] = useState(false);
  const widowSize = useWindowSize();
  const { width: workWidth } = useElementDimensions(editDialogContentId, elementDrawn);
  const [splitRatios, setSplitRatios] = useState<number[]>(getSplitRatios(userCondition));
  const [splitSizes, setSplitSizes] = useState<number[]>();
  const [noteDisplay, setNoteDisplay] = useState(false);
  const [noteLeft, setNoteLeft] = useState(0);
  const [noteTransition, setNoteTransition] = useState<string>();
  const noteTransitionTime = 180;
  const [splitUpdated, setSplitUpdated] = useState(true);

  useEffect(() => {
    const attributeHash: EditTemplateAttributeDbHashType = {};
    attributeIds.forEach((id, ix) => attributeHash[id] = attributeValues[ix]);
    const shapeHash: EditTemplateShapeDbHashType = {};
    shapeIds.forEach((id, ix) => shapeHash[id] = shapeValues[ix]);
    setTemplate(templateValue);
    setAttributes(attributeHash);
    setShapes(shapeHash);
  }, [existingTemplate]);// templateValueとかattributeIdsをキーにすると無限ループ

  const onExit = () => {
    setTemplate(undefined);
    setAttributes({});
    setShapes({});
    onClose();
  }
  const dragSplit = (sizes: number[]) => {
    if (!userConditionId || !userCondition) return;
    setSplitUpdated(false);
    const newRatio = deepCopy(userCondition.workNoteRatios);
    const newSizes = sizes;
    newRatio.cardTemplateDesign = newSizes;
    dispatch(updateUserCondition({ id: userConditionId, workNoteRatios: newRatio }));
    setSplitRatios(newRatio.cardTemplateDesign);
    setTimeout(() => setSplitUpdated(true), 1);
  }
  const toggleNote = (value: boolean) => {
    if (!userConditionId || !userCondition) return;
    setSplitUpdated(false);
    const newNoteDisplay = deepCopy(userCondition.noteDisplay);
    newNoteDisplay.cardTemplateDesign = value;

    if (workWidth) setNoteLeft(workWidth * userCondition.workNoteRatios.cardTemplateDesign[1] / 100);

    setNoteTransition(`${noteTransitionTime}ms ease-out`);
    if (value) setNoteDisplay(value);

    if (value) {
      dispatch(updateUserCondition({ id: userConditionId, noteDisplay: newNoteDisplay }));
      setTimeout(() => setNoteLeft(0), 1);
    } else {
      setTimeout(() => {
        dispatch(updateUserCondition({ id: userConditionId, noteDisplay: newNoteDisplay }));
      }, noteTransitionTime);
    }
    setTimeout(() => {
      setNoteTransition(undefined);
      setSplitUpdated(true);
    }, noteTransitionTime);
  }

  useEffect(() => {
    if (!userCondition || !template) return;
    setSplitRatios(getSplitRatios(userCondition));
    setNoteDisplay(userCondition.noteDisplay.cardTemplateDesign && template?.kind === 'common');
  }, [userCondition, id, template])

  useEffect(() => {
    setSplitRatios(getSplitRatios(userCondition));
  }, [noteDisplay])

  useEffect(() => {
    setElementDrawn(false);
    setTimeout(() => setElementDrawn(true), 1);
  }, [useElementDimensions(editDialogContentId), id])

  useEffect(() => {
    if (splitSizes && noteDisplay) dragSplit(splitSizes);
  }, [splitSizes])

  return (
    <>
      <Dialog open={open} onClose={onClose} onExited={onExit} onEnter={() => setTemplate(templateValue)} fullWidth={true} maxWidth="xl" >
        {templateId? <DialogTitle>カードテンプレートを編集します</DialogTitle> :
             <DialogTitle>カードテンプレートを新規作成します</DialogTitle> }
        <DialogContent id={editDialogContentId} style={{ height: template?.kind === 'common' ? widowSize.innerHeight - 64 - 64 - 116.5 : undefined, overflow: 'hidden' }}>
          <Split
            expandToMin={false}
            gutterSize={5}
            gutterAlign="center"
            snapOffset={30}
            dragInterval={1}
            direction="horizontal"
            sizes={splitRatios}
            onDrag={sizes => setSplitSizes(sizes)}
            className={noteDisplay ? classes.split : classes.disableSplit}
          >
            <div className={classes.workPanel}>
              { template &&
                <EditTemplateCard id={id} template={template} setTemplate={setTemplate}
                  attributes={attributes} setAttributes={setAttributes}
                  shapes={shapes} setShapes={setShapes}
                  splitUpdated={splitUpdated}
                />
              }
              { template?.kind === 'common' &&
                <OpenNoteButton noteDisplay={noteDisplay} openNote={() => toggleNote(true)} />
              }
            </div>
            <div className={classes.notePanel} style={{ display: noteDisplay ? 'block' : 'none', left: noteLeft, transition: noteTransition }}>
              <Note
                reference="cardTemplate" referenceId={id}
                toggleNote={() => toggleNote(false)}
              />
            </div>
          </Split>
        </DialogContent>
        <DialogActions>
          <IconButton color='inherit' onClick={() => setPermissionAction({show:true})}>
            <LockIcon />
          </IconButton>
          <Button onClick={onClose} variant='text' color='primary'>
            キャンセル
          </Button>
          <Button variant='text' color='primary' onClick={() => setSaveAction({show:true})}>
            保存する
          </Button>
        </DialogActions>
      </Dialog>
      <PermissionDialog open={permissionAction.show} onClose={() => setPermissionAction({show:false})}/>
      { template &&
        <SaveTemplateDialog open={saveAction.show} onClose={() => setSaveAction({show:false})}
                          id={id} template={template} setTemplate={setTemplate}
                          attributes={attributes} shapes={shapes}
                          paletteId={paletteId} cardTemplatesLength={cardTemplatesLength}
                          onSaveClose={() => { setSaveAction({show:false})
                                                 onClose()}} />
      }
    </>
  );
}

export default EditTemplateDialog;
